import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView, animate, AnimatePresence, useSpring, useReducedMotion } from "framer-motion";
import {
  ArrowRight, Users, Handshake, TrendingUp, FileText, Home as HomeIcon,
  Hammer, Search, Landmark, KeyRound, BarChart3,
  Coins, Scale, Calculator, Building2, ChevronDown,
} from "lucide-react";
import { Layout } from "../components/Layout";
import { Reveal, MaskLine, EASE } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { SectionHead } from "../components/SectionHead";
import { CtaSection } from "../components/CtaSection";
import { Gallery } from "../components/Gallery";
import { Partners } from "../components/Partners";
import { CinematicBreak } from "../components/CinematicBreak";
import { usePageMeta } from "../hooks/usePageMeta";
import { IMAGES, CINEMATIC, SERVICES, PROCESS_STEPS, NETWORK, TESTIMONIALS, CLIENT_STORIES } from "../data/site";
import { useContent } from "../context/ContentContext";
import { fileUrl } from "../lib/files";
import { ICONS } from "../data/icons";

const STEP_ICONS = [Search, HomeIcon, Landmark, Hammer, KeyRound, BarChart3];
const NET_ICONS = [Coins, Scale, Calculator, Users, Building2];
const SVC_ICONS = [FileText, HomeIcon, Hammer, TrendingUp];

const DEFAULT_EYEBROW = "Property Advice · Investment Support · Professional Connections";
const DEFAULT_SUB =
  "At Elin's Consultancy, we provide independent tenant and leasehold advice, help investors find the right properties and coordinate the professionals you need — from finance and legal support to refurbishment and tenant finding.";

const Hero = () => {
  const ref = useRef(null);
  const content = useContent();
  const heroC = content?.hero || {};
  const heroBg = heroC.bg_image ? fileUrl(heroC.bg_image) : CINEMATIC.heroHome;
  const prefersReduced = useReducedMotion();

  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  // Smooth the raw scroll progress so transforms feel cinematic, not jittery.
  const smooth = useSpring(scrollYProgress, { stiffness: 110, damping: 24, mass: 0.35 });

  // Scroll-driven 3D depth: the image zooms in (scale) and recedes slightly on Z,
  // while the headline block gracefully scales + fades out. Transforms + opacity only
  // → GPU-accelerated. On reduced-motion we hold the initial state.
  const imgScale = useTransform(smooth, [0, 1], prefersReduced ? [1, 1] : [1, 1.32]);
  const imgTranslateZ = useTransform(smooth, [0, 1], prefersReduced ? [0, 0] : [0, 120]);
  const imgY = useTransform(smooth, [0, 1], prefersReduced ? ["0%", "0%"] : ["0%", "14%"]);
  const textScale = useTransform(smooth, [0, 0.6], prefersReduced ? [1, 1] : [1, 0.92]);
  const textY = useTransform(smooth, [0, 0.6], prefersReduced ? [0, 0] : [0, -40]);
  const textOpacity = useTransform(smooth, [0, 0.55, 0.8], prefersReduced ? [1, 1, 1] : [1, 0.4, 0]);
  const overlayOpacity = useTransform(smooth, [0, 1], prefersReduced ? [1, 1] : [1, 1.2]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="hero-perspective relative min-h-[100svh] bg-forest-deep text-cream overflow-hidden flex items-center grain"
    >
      <motion.div
        style={{ scale: imgScale, translateZ: imgTranslateZ, y: imgY }}
        className="hero-3d absolute inset-0 will-change-transform"
      >
        <img
          src={heroBg}
          alt="The London skyline at dusk, warm lights reflecting on the River Thames"
          fetchPriority="high"
          className="w-full h-[118%] object-cover kenburns"
        />
      </motion.div>
      <motion.div
        aria-hidden="true"
        style={{ opacity: overlayOpacity }}
        className="absolute inset-0 bg-gradient-to-r from-forest-deep/95 via-forest/80 to-forest-deep/40 will-change-[opacity]"
      />
      <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-forest-deep/95 via-transparent to-forest-deep/50" />
      <div aria-hidden="true" className="hero-glow" />

      <motion.div
        style={{ opacity: textOpacity, scale: textScale, y: textY }}
        className="wrap-x relative z-[2] w-full pt-32 pb-28 will-change-transform"
      >
        <Reveal y={14}>
          <p className="eyebrow text-gold-soft" data-testid="hero-eyebrow">{heroC.eyebrow || DEFAULT_EYEBROW}</p>
        </Reveal>
        <h1 className="font-serif font-medium tracking-tight text-5xl sm:text-6xl lg:text-[88px] leading-[1.0] mt-8 max-w-4xl">
          <MaskLine delay={0.1}>{heroC.line1 || "Your Property"}</MaskLine>
          <MaskLine delay={0.22}>{heroC.line2 || "Journey,"}</MaskLine>
          <MaskLine delay={0.34}>
            {heroC.line3_pre || "Made"}{" "}
            <em className="italic font-normal text-gold-soft">{heroC.line3_accent || "Simpler."}</em>
          </MaskLine>
        </h1>
        <Reveal delay={0.55}>
          <p className="mt-9 max-w-md text-[15.5px] leading-[1.85] text-cream/75">{heroC.subheading || DEFAULT_SUB}</p>
        </Reveal>
        <Reveal delay={0.7} className="mt-11 flex flex-wrap gap-4">
          <Link to="/contact" data-testid="hero-book-button" className="btn-gold">
            Book a Consultation <ArrowRight size={15} strokeWidth={2.2} />
          </Link>
          <Link to="/how-we-work" data-testid="hero-explore-button" className="btn-ghost-light">
            Explore Our Services
          </Link>
        </Reveal>
      </motion.div>

      <motion.div
        data-testid="hero-stat-card"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 1, ease: EASE }}
        style={{ opacity: textOpacity }}
        className="absolute z-[3] bottom-24 right-10 xl:right-16 hidden lg:block bg-cream/95 backdrop-blur text-forest-deep px-7 py-6 shadow-[0_24px_50px_rgba(0,0,0,0.35)] animate-float-soft"
      >
        <p className="font-serif text-[32px] leading-none text-forest">End-to-End</p>
        <p className="text-[10px] tracking-[0.22em] uppercase font-bold text-sage mt-2.5">
          From Search to Rental
        </p>
      </motion.div>

      <motion.div
        aria-hidden="true"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        style={{ opacity: textOpacity }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-[3] flex flex-col items-center gap-2 text-cream/60"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase font-semibold">Scroll</span>
        <ChevronDown size={16} className="animate-bounce text-gold-soft" />
      </motion.div>
    </section>
  );
};

const CredibilityTrio = () => {
  const items = [
    { icon: Users, title: "Independent Guidance", text: "Clear, practical advice you can trust — with no ties to lenders, agents or developers." },
    { icon: Handshake, title: "Connected Professionals", text: "Access to a trusted network of advisers, solicitors and specialists." },
    { icon: TrendingUp, title: "End-to-End Support", text: "From property search to rental — one coordinated journey." },
  ];
  return (
    <section data-testid="credibility-section" className="bg-cream">
      <div className="wrap-x py-16 sm:py-20 grid grid-cols-1 md:grid-cols-3">
        {items.map((item, i) => (
          <Reveal
            key={item.title}
            delay={i * 0.12}
            className={`py-8 md:py-0 md:px-11 ${i > 0 ? "border-t md:border-t-0 md:border-l border-forest/15" : ""} ${i === 0 ? "md:pl-0" : ""}`}
          >
            <item.icon size={34} strokeWidth={1.3} className="text-gold" />
            <h3 className="font-serif text-2xl font-semibold text-forest mt-5 mb-2.5">{item.title}</h3>
            <p className="text-[13.5px] text-sage leading-relaxed">{item.text}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const Pathways = () => {
  const paths = [
    {
      num: "i",
      eyebrow: "For Tenants & Leaseholders",
      title: <>Independent<br />Property Advice</>,
      text: "Practical, independent advice to help you understand your rights, navigate tenancy or leasehold issues and make informed decisions.",
      cta: "Get Property Advice",
      to: "/tenant-leasehold-advice",
      img: IMAGES.livingWarm,
      alt: "Warm, naturally lit living room with soft neutral furnishings",
      testid: "pathway-tenant",
    },
    {
      num: "ii",
      eyebrow: "For Investors",
      title: <>Property Sourcing<br />&amp; Investment Support</>,
      text: "We help investors find suitable property opportunities and coordinate the expertise needed to turn them into successful investments.",
      cta: "Find an Investment Property",
      to: "/investors",
      img: IMAGES.penthouse,
      alt: "Bright modern apartment interior with tall windows",
      testid: "pathway-investor",
    },
  ];
  return (
    <section data-testid="pathways-section" className="bg-cream pb-20 sm:pb-24">
      <div className="wrap-x grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
        {paths.map((p, i) => (
          <Reveal key={p.testid} delay={i * 0.15}>
            <article
              data-testid={p.testid}
              className="group relative bg-cream-3 border border-forest/10 overflow-hidden h-full flex flex-col transition-colors duration-500 hover:border-gold/60"
            >
              <div className="h-44 sm:h-52 overflow-hidden">
                <img
                  src={p.img}
                  alt={p.alt}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]"
                />
              </div>
              <div className="p-8 sm:p-12 flex flex-col flex-1">
                <span aria-hidden="true" className="absolute top-48 sm:top-56 right-8 font-serif italic text-[110px] leading-none text-forest/[0.06] select-none">
                  {p.num}
                </span>
                <p className="text-[10.5px] tracking-[0.3em] uppercase font-bold text-gold-dark">{p.eyebrow}</p>
                <h3 className="font-serif text-3xl sm:text-[38px] font-medium text-forest leading-[1.1] mt-4 mb-4">{p.title}</h3>
                <p className="text-sm leading-[1.75] text-sage max-w-sm mb-8">{p.text}</p>
                <div className="mt-auto">
                  <Link to={p.to} data-testid={`${p.testid}-button`} className="btn-forest">
                    {p.cta} <ArrowRight size={14} strokeWidth={2.2} />
                  </Link>
                </div>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const Services = () => {
  const content = useContent();
  const services = content?.services?.length ? content.services : SERVICES;
  return (
    <section data-testid="services-section" className="bg-cream-2">
      <SectionHead
        eyebrow="Our Services"
        title="How We Can Help"
        text="We offer a complete range of property consultancy services, bringing together the right advice, opportunities and professional support for your needs."
      />
      <div className="wrap-x pb-20 sm:pb-24 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
        {services.map((s, i) => {
          const Icon = ICONS[s.icon] || SVC_ICONS[i % SVC_ICONS.length];
          return (
            <Reveal key={s.id || s.num} delay={i * 0.1}>
              <article
                data-testid={`service-card-${s.num}`}
                className="group bg-cream border border-forest/10 p-8 flex flex-col min-h-[340px] h-full transition-all duration-500 hover:border-gold/60 hover:-translate-y-1"
              >
                <div className="flex items-center justify-between">
                  <span className="font-serif italic text-xl text-gold-dark">{s.num}</span>
                  <Icon size={30} strokeWidth={1.2} className="text-gold" />
                </div>
                <div className="w-full h-px bg-forest/15 my-6" />
                <h3 className="font-serif text-[26px] font-semibold text-forest leading-[1.15]">{s.title}</h3>
                <p className="text-[13px] leading-[1.7] text-sage mt-3 flex-1">{s.text}</p>
                <Link to={s.to || "/contact"} data-testid={`service-link-${s.num}`} className="link-more mt-6">
                  Find Out More <ArrowRight size={13} className="text-gold-dark" />
                </Link>
              </article>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
};

const CountUp = ({ to, suffix = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration: 1.8,
      ease: EASE,
      onUpdate: (v) => setVal(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, to]);
  return <span ref={ref}>{val}{suffix}</span>;
};

const HIGHLIGHTS = [
  { to: 6, label: "Step Guided Process" },
  { to: 5, label: "Professional Partners" },
  { to: 1, label: "Point of Contact" },
  { to: 100, suffix: "%", label: "Independent Advice" },
];

const Highlights = () => (
  <section data-testid="highlights-section" className="bg-cream border-t border-forest/10">
    <div className="wrap-x grid grid-cols-2 lg:grid-cols-4">
      {HIGHLIGHTS.map((h, i) => (
        <Reveal
          key={h.label}
          delay={i * 0.1}
          className={`py-12 sm:py-16 text-center px-4 border-forest/12 ${i % 2 === 1 ? "border-l" : ""} ${i > 1 ? "border-t" : ""} ${i > 0 ? "lg:border-l" : ""} lg:border-t-0`}
        >
          <p className="font-serif text-5xl sm:text-6xl text-forest leading-none">
            <CountUp to={h.to} suffix={h.suffix || ""} />
          </p>
          <p className="text-[10.5px] tracking-[0.26em] uppercase font-bold text-sage mt-4">{h.label}</p>
        </Reveal>
      ))}
    </div>
  </section>
);

const Process = () => (
  <section data-testid="process-section" className="bg-forest text-cream relative grain">
    <SectionHead
      dark
      eyebrow="A Clear and Supported Process"
      title="From Property Search to Rental"
      text="We help you at every stage, coordinating the right professionals along the way."
    />
    <div className="wrap-x relative z-[2] pb-20 sm:pb-24 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-y-12">
      {PROCESS_STEPS.map((step, i) => {
        const Icon = STEP_ICONS[i];
        return (
          <Reveal key={step.num} delay={i * 0.08} className="relative px-4 text-center">
            {i < PROCESS_STEPS.length - 1 && (
              <span aria-hidden="true" className="hidden xl:block absolute top-[34px] -right-3.5 w-7 h-px hairline-gold" />
            )}
            <div className="w-[68px] h-[68px] rounded-full border border-gold mx-auto flex items-center justify-center">
              <Icon size={26} strokeWidth={1.3} className="text-gold-soft" />
            </div>
            <p className="text-[11px] tracking-[0.3em] text-gold font-bold mt-5">{step.num}</p>
            <h3 className="font-serif text-xl font-semibold mt-2 mb-2.5">{step.title}</h3>
            <p className="text-xs leading-[1.65] text-cream/55">{step.text}</p>
          </Reveal>
        );
      })}
    </div>
  </section>
);

const NetworkSection = () => (
  <section data-testid="network-section" className="bg-cream">
    <SectionHead
      eyebrow="Our Professional Network"
      title="Trusted Experts, Introduced by Elin's Consultancy"
      text="We work with a range of trusted professionals to ensure you have the right support for your property journey."
    />
    <div className="wrap-x pb-20 sm:pb-24 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-y-12">
      {NETWORK.map((n, i) => {
        const Icon = NET_ICONS[i];
        return (
          <Reveal
            key={n.name}
            delay={i * 0.08}
            className={`px-6 text-center ${i > 0 ? "xl:border-l border-forest/15" : ""}`}
          >
            <Icon size={34} strokeWidth={1.2} className="text-gold mx-auto mb-4" />
            <h3 className="font-serif text-xl font-semibold text-forest mb-2">{n.name}</h3>
            <p className="text-xs leading-[1.65] text-sage">{n.text}</p>
          </Reveal>
        );
      })}
    </div>
  </section>
);

const AboutTeaser = () => (
  <section data-testid="about-teaser" className="bg-cream">
    <div className="wrap-x py-20 sm:py-24 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
      <Reveal>
        <div className="relative h-[340px] sm:h-[440px] overflow-hidden">
          <img
            src={IMAGES.loungeConsult}
            alt="A calm consultation lounge with comfortable seating and warm light"
            loading="lazy"
            decoding="async"
            className="w-full h-full object-cover"
          />
          <span aria-hidden="true" className="absolute inset-4 border border-cream/50 pointer-events-none" />
        </div>
      </Reveal>
      <Reveal delay={0.15}>
        <p className="eyebrow text-gold-dark">About Elin&rsquo;s Consultancy</p>
        <h2 className="font-serif text-4xl sm:text-[46px] font-medium text-forest leading-[1.08] mt-5 mb-6">
          A Personal,<br />Professional Approach
        </h2>
        <p className="text-[14.5px] leading-[1.8] text-sage max-w-md mb-9">
          Elin&rsquo;s Consultancy was founded to make the property journey simpler, clearer and more
          accessible. We provide independent guidance and work with a trusted network of professionals
          to help tenants, leaseholders and investors achieve their property goals with confidence.
        </p>
        <Link to="/about" data-testid="about-teaser-button" className="btn-forest">
          Find Out More About Us <ArrowRight size={14} strokeWidth={2.2} />
        </Link>
      </Reveal>
    </div>
  </section>
);

const ClientStories = () => (
  <section data-testid="client-stories-section" className="bg-cream-2">
    <SectionHead
      eyebrow="Client Stories"
      title="Real Journeys, From First Call to Closing"
      text="Every property journey is different. Here is how three of them unfolded — stage by stage."
    />
    <div className="wrap-x pb-10 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
      {CLIENT_STORIES.map((s, i) => (
        <Reveal key={s.title} delay={i * 0.1} className="h-full">
          <article
            data-testid={`story-card-${i + 1}`}
            className="group bg-cream-3 border border-forest/10 overflow-hidden h-full flex flex-col transition-colors duration-500 hover:border-gold/60"
          >
            <div className="relative h-52 overflow-hidden">
              <img
                src={s.img}
                alt={s.alt}
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.05]"
              />
              <span className="absolute bottom-4 left-4 bg-forest-deep/85 backdrop-blur-sm text-gold-soft text-[10px] tracking-[0.24em] uppercase font-bold px-3.5 py-2">
                {s.tag}
              </span>
            </div>
            <div className="p-8 flex flex-col flex-1">
              <h3 className="font-serif text-[26px] font-semibold text-forest leading-[1.2]">{s.title}</h3>
              <ol className="my-6">
                {s.journey.map((j, idx) => (
                  <li
                    key={j}
                    className="flex items-center gap-3.5 py-2.5 border-b border-forest/10 last:border-0 text-[11.5px] tracking-[0.1em] uppercase font-semibold text-forest/75"
                  >
                    <span className="font-serif italic text-gold-dark text-base normal-case tracking-normal">
                      {String(idx + 1).padStart(2, "0")}
                    </span>
                    {j}
                  </li>
                ))}
              </ol>
              <p className="text-[13px] leading-[1.7] text-sage flex-1">{s.text}</p>
              <p className="font-serif italic text-forest text-lg leading-snug mt-6 pt-5 border-t border-gold/40">
                {s.outcome}
              </p>
            </div>
          </article>
        </Reveal>
      ))}
    </div>
    <div className="wrap-x pb-20 sm:pb-24">
      <Reveal>
        <p className="text-[11px] tracking-[0.06em] text-sage/70 text-center">
          Stories shared with client permission; names and identifying details anonymised.
        </p>
      </Reveal>
    </div>
  </section>
);

const Testimonials = () => {
  const content = useContent();
  const testimonials = content?.testimonials?.length ? content.testimonials : TESTIMONIALS;
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [reduced, setReduced] = useState(false);
  const count = testimonials.length;

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = () => setReduced(mq.matches);
    handler();
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);

  useEffect(() => {
    if (count <= 1 || paused || reduced) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % count), 6500);
    return () => clearInterval(id);
  }, [count, paused, reduced]);

  // Keep index in range if admin removes a testimonial
  useEffect(() => {
    if (index >= count) setIndex(0);
  }, [index, count]);

  if (count === 0) return null;

  const go = (i) => setIndex(((i % count) + count) % count);
  const active = testimonials[index];

  return (
    <section data-testid="testimonials-section" className="bg-cream">
      <SectionHead eyebrow="What Our Clients Say" title="Trusted by Tenants, Leaseholders and Investors" />
      <div className="wrap-x pb-20 sm:pb-24">
        <div
          data-testid="testimonials-carousel"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocus={() => setPaused(true)}
          onBlur={() => setPaused(false)}
          className="relative max-w-3xl mx-auto"
        >
          <div className="relative min-h-[320px] sm:min-h-[280px] flex items-center justify-center">
            <AnimatePresence mode="wait">
              <motion.blockquote
                key={active.id || `${active.name}-${index}`}
                data-testid={`testimonial-${index + 1}`}
                initial={reduced ? false : { opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={reduced ? { opacity: 0 } : { opacity: 0, y: -14 }}
                transition={{ duration: 0.6, ease: [0.4, 0.0, 0.2, 1] }}
                className="text-center px-2 sm:px-6"
                aria-live="polite"
              >
                <span
                  aria-hidden="true"
                  className="font-serif italic text-[92px] leading-[0.5] text-gold block mb-6"
                >
                  &ldquo;
                </span>
                <p className="font-serif italic text-[22px] sm:text-[26px] leading-[1.5] text-forest">
                  {active.quote}
                </p>
                <footer className="mt-9">
                  <p className="text-[11px] tracking-[0.26em] uppercase font-bold text-ink">{active.name}</p>
                  {active.role && (
                    <p className="text-[11px] tracking-[0.14em] text-sage mt-1.5">{active.role}</p>
                  )}
                </footer>
              </motion.blockquote>
            </AnimatePresence>
          </div>

          {count > 1 && (
            <div
              role="tablist"
              aria-label="Testimonials"
              className="mt-10 flex items-center justify-center gap-5"
              data-testid="testimonials-dots"
            >
              {testimonials.map((t, i) => (
                <button
                  key={t.id || t.name}
                  type="button"
                  role="tab"
                  aria-selected={i === index}
                  aria-label={`Show testimonial ${i + 1} of ${count}`}
                  data-testid={`testimonials-dot-${i + 1}`}
                  onClick={() => go(i)}
                  className="group relative py-3"
                >
                  <span
                    className={`block h-[2px] transition-all duration-500 ${
                      i === index
                        ? "w-10 bg-gold"
                        : "w-6 bg-forest/25 group-hover:bg-forest/50"
                    }`}
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  usePageMeta(
    "Elin's Consultancy | Property & Leasehold Advice",
    "Independent tenant and leasehold advice, property sourcing for investors, refurbishment coordination and a trusted professional network."
  );
  return (
    <Layout>
      <Hero />
      <Marquee />
      <CredibilityTrio />
      <Pathways />
      <Services />
      <Gallery />
      <Process />
      <NetworkSection />
      <Highlights />
      <CinematicBreak
        image={CINEMATIC.streetCrescent}
        alt="A golden-hour residential London crescent lined with period townhouses"
        caption="Our Promise"
        quote="One point of contact. The right professionals. A property journey you actually enjoy."
      />
      <AboutTeaser />
      <ClientStories />
      <Testimonials />
      <CtaSection />
      <Partners />
    </Layout>
  );
}
